import { Button, Paper, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { Field } from 'src/API';
import { FieldInput, FIELD_TYPES } from 'src/api/process-module';
import { TEST_IDS } from 'src/test-ids';
import { RootCanvasModeSelector } from './RootCanvasModeSelector';
import { CanvasTemplateSelector } from './CanvasTemplateSelector';
import { RootFieldSelector } from './RootFieldSelector';
import { useProcessModuleRootCanvas } from '../hooks/useProcessModuleRootCanvas';

interface ProcessModuleRootCanvasFieldProps {
  field: FieldInput & Partial<Field>;
  index: number;
  handleFieldChange: (
    index: number,
    field: Partial<Partial<FieldInput & Partial<Field>> & { moduleId: string }>,
  ) => void;
  isSubmitting: boolean;
  isUsingExistingField: boolean;
  moduleId: string;
  onSelectExistingField?: () => void;
  allFormFields?: (FieldInput & Partial<Field>)[];
}

export const ProcessModuleRootCanvasField = ({
  field,
  index,
  handleFieldChange,
  isSubmitting,
  moduleId,
  allFormFields,
}: ProcessModuleRootCanvasFieldProps) => {
  // Use custom hook for all root canvas logic
  const rootCanvas = useProcessModuleRootCanvas(
    field,
    moduleId,
    (updates) => handleFieldChange(index, updates),
    allFormFields,
  );

  // Early return for wrong field type
  if (field.type !== FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS) {
    return null;
  }

  // Handle template selection
  const handleTemplateChange = (templateId: string) => {
    rootCanvas.updateRootCanvasProperties({ templateId });
  };

  // Handle button text change - store in override.label (separate from field label)
  const handleButtonTextChange = (buttonText: string) => {
    rootCanvas.updateRootCanvasProperties({
      override: {
        ...rootCanvas.properties.override,
        label: buttonText,
      },
    });
  };

  // Get the current button text for display in the TextField
  // Button text comes from override.label, field label is separate
  const currentButtonText = rootCanvas.properties.override?.label ?? 'Open Canvas';

  // Check if canvas is already configured
  const isCanvasConfigured = rootCanvas.properties.canvasId && rootCanvas.properties.canvasCreated;

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Root Canvas Configuration</Typography>

      {/* Show canvas info if already configured */}
      {isCanvasConfigured && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50', borderColor: 'success.main' }}>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography variant="body2" color="success.dark" fontWeight="bold">
                ✓ Canvas Configured {rootCanvas.state.selectedRootField ? '(Referenced)' : '(Root Canvas)'}
              </Typography>
              {field.id.startsWith('temp-') && (
                <Typography variant="body2" color="warning.dark" fontWeight="bold">
                  ⚠️ Remember to click the "Save Field" button below to persist this configuration
                </Typography>
              )}
              {/* Show which root field this references if using reference mode */}
              {rootCanvas.state.selectedRootField && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Referenced field: <strong>{rootCanvas.state.selectedRootField.fieldLabel}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In form: <strong>{rootCanvas.state.selectedRootField.formName}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Template: <strong>{rootCanvas.state.selectedRootField.templateId || 'None'}</strong>
                  </Typography>
                </>
              )}
              {/* Show template if it's a root canvas */}
              {!rootCanvas.state.selectedRootField && (
                <Typography variant="body2" color="text.secondary">
                  Template: <strong>{rootCanvas.properties.templateId || 'None selected'}</strong>
                </Typography>
              )}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => {
                rootCanvas.updateRootCanvasProperties({
                  canvasId: '',
                  canvasCreated: false,
                  templateId: '',
                  referencedFieldId: '',
                  isRootField: false,
                });
                rootCanvas.setCanvasMode('root');
                // Note: setCanvasMode already clears selectedRootField
              }}
              disabled={isSubmitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              Reset Canvas Configuration
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Only show mode selection and canvas options if canvas is NOT configured */}
      {!isCanvasConfigured && (
        <>
          {/* Canvas mode selection */}
          <RootCanvasModeSelector
            mode={rootCanvas.state.mode}
            hasAvailableRootFields={rootCanvas.hasAvailableRootFields}
            isSubmitting={isSubmitting}
            onModeChange={rootCanvas.setCanvasMode}
          />

          {/* Root field selection for reference mode */}
          {rootCanvas.state.mode === 'reference' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <RootFieldSelector
                currentReferencedFieldId={rootCanvas.properties.referencedFieldId}
                rootFields={rootCanvas.state.rootFields.filter((rf) => rf.fieldId !== field.id)}
                isSubmitting={isSubmitting}
                onRootFieldSelect={rootCanvas.selectRootField}
              />
            </Paper>
          )}

          {/* Root canvas creation */}
          {rootCanvas.state.mode === 'root' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <CanvasTemplateSelector
                templateId={rootCanvas.properties.templateId || ''}
                canvasCreated={rootCanvas.properties.canvasCreated || false}
                isCreating={false}
                isSubmitting={isSubmitting}
                canCreateCanvas={rootCanvas.canCreateRootCanvas}
                onTemplateChange={handleTemplateChange}
                onCreateCanvas={rootCanvas.configureRootCanvas}
              />
            </Paper>
          )}
        </>
      )}

      {/* Button text configuration */}
      <TextField
        fullWidth
        label="Button Text"
        value={currentButtonText}
        onChange={(e) => handleButtonTextChange(e.target.value)}
        disabled={isSubmitting}
        helperText="Text displayed on the button that opens the canvas"
        placeholder={field.label || 'Open Canvas'}
        inputProps={{ 'data-testid': TEST_IDS.CANVAS_FIELD.BUTTON_TEXT_INPUT }}
      />

      {/* Error display */}
      {rootCanvas.state.error && (
        <Typography color="error" variant="body2">
          {rootCanvas.state.error}
        </Typography>
      )}
    </Stack>
  );
};
